// Base service class for consistent error handling and logging
export class ServiceError extends Error {
  constructor(
    message: string,
    public context: string,
    public originalError?: any,
    public code?: string
  ) {
    super(message);
    this.name = 'ServiceError';
  }
}

export class AuthError extends ServiceError {
  constructor(message: string, originalError?: any, code?: string) {
    super(message, 'Authentication', originalError, code);
    this.name = 'AuthError';
  }
}

export class DatabaseError extends ServiceError {
  constructor(message: string, originalError?: any, code?: string) {
    super(message, 'Database', originalError, code);
    this.name = 'DatabaseError';
  }
}

export class NetworkError extends ServiceError {
  constructor(message: string, originalError?: any) {
    super(message, 'Network', originalError, 'NETWORK_ERROR');
    this.name = 'NetworkError';
  }
}

export abstract class BaseService {
  protected handleError(error: any, context: string): never {
    console.error(`${context} Error:`, error);
    
    // Handle different types of errors
    if (error?.message?.includes('network') || error?.message?.includes('fetch')) {
      throw new NetworkError('Network connection failed. Please check your internet connection.', error);
    }
    
    const errorCode = typeof error?.code === 'string' ? error.code : '';
    const errorMessage = error?.message || '';
    
    if (errorMessage.includes('auth') || errorCode.includes('auth')) {
      throw new AuthError(error.message || 'Authentication failed', error, error.code);
    }
    
    if (errorMessage.includes('database') || errorCode.includes('PGRST')) {
      throw new DatabaseError(error.message || 'Database operation failed', error, error.code);
    }
    
    // Generic service error
    throw new ServiceError(
      error.message || 'An unexpected error occurred',
      context,
      error,
      error.code
    );
  }

  protected validateRequired(value: any, fieldName: string): void {
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      throw new ServiceError(`${fieldName} is required`, 'Validation');
    }
  }

  protected validateEmail(email: string): void {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      throw new ServiceError('Invalid email format', 'Validation');
    }
  }
}